/**
 * GhostIconButton — the single app-wide per-row action affordance (edit,
 * delete, deactivate, reorder, …). A Ø28 circular ghost action (DESIGN-SPEC
 * §2.8 row-action tier): bg +1 luminance step over the parent row at rest,
 * one more step on hover. `danger` keeps a NEUTRAL glyph at rest (severity is
 * carried by the tooltip, not colour adjacency) and turns red glyph on red
 * tint on hover — the chip tint formula. The visual circle stays 28px but the
 * interactive hitbox is 44×44 (content-box padding extends the hit area; the
 * negative margin keeps the 28px layout footprint so dense ledger rows don't
 * grow; background-clip pins the fill to the visible circle).
 * Shows an INSTANT tooltip on hover — rendered in a portal with fixed
 * positioning so it is never clipped by a scrolling table or a transformed
 * (animated) row, and not subject to the browser's native `title` delay.
 * `tooltip` is the short verb shown on hover (e.g. "Delete"); `label` is the
 * full accessible name for screen readers.
 */
import { useState } from 'react';
import { createPortal } from 'react-dom';

export function GhostIconButton({ tooltip, label, onClick, danger, disabled, ariaHasPopup, ariaExpanded, children }: {
  tooltip: string;
  label?: string;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  /** Menu-button ARIA state passthrough — set when this trigger opens a popover/menu (DashboardSwitcher trigger idiom). */
  ariaHasPopup?: React.AriaAttributes['aria-haspopup'];
  ariaExpanded?: boolean;
  children: React.ReactNode;
}) {
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);

  return (
    <>
      <button
        type="button"
        aria-label={label ?? tooltip}
        aria-haspopup={ariaHasPopup}
        aria-expanded={ariaExpanded}
        title={tooltip}
        onClick={onClick}
        disabled={disabled}
        className="pressable focus-ring"
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 28, height: 28, padding: 8, margin: -8, boxSizing: 'content-box',
          border: 'none', borderRadius: 'var(--radius-pill)', backgroundClip: 'content-box',
          // QA R2 regression fix: `backgroundColor` (not the `background`
          // shorthand) — React sets style props in object-key order, and the
          // shorthand resets `background-clip` to its initial `border-box`
          // whenever it's assigned AFTER `backgroundClip` in the object (as
          // this one was), so the fill painted the full 44x44 hitbox instead
          // of the visible 28px circle. `backgroundColor` never touches clip.
          backgroundColor: 'var(--surface-2)', cursor: disabled ? 'not-allowed' : 'pointer',
          color: 'var(--text-subtle)', fontSize: 13,
          transition: 'color var(--dur-fast) var(--ease-out), background-color var(--dur-fast) var(--ease-out)',
          opacity: disabled ? 0.4 : 1,
        }}
        onMouseEnter={(e) => {
          const r = e.currentTarget.getBoundingClientRect();
          setPos({ x: r.left + r.width / 2, y: r.top });
          if (disabled) return;
          const el = e.currentTarget as HTMLButtonElement;
          // Hover = +1 ladder step over the resting circle; danger reveals the
          // chip tint formula (red glyph on red tint).
          el.style.backgroundColor = danger ? 'var(--health-red-bg)' : 'var(--surface-3)';
          el.style.backgroundClip = 'content-box';
          el.style.color = danger ? 'var(--health-red)' : 'var(--text)';
        }}
        onMouseLeave={(e) => {
          setPos(null);
          const el = e.currentTarget as HTMLButtonElement;
          el.style.backgroundColor = 'var(--surface-2)';
          el.style.backgroundClip = 'content-box';
          el.style.color = 'var(--text-subtle)';
        }}
      >
        {children}
      </button>
      {pos && createPortal(
        <span
          role="tooltip"
          style={{
            position: 'fixed', left: pos.x, top: pos.y - 4, transform: 'translate(-50%, -100%)',
            background: 'var(--tooltip-bg)', color: 'var(--tooltip-fg)',
            fontFamily: 'var(--font-sans)', fontSize: 'var(--text-2xs)', fontWeight: 600,
            letterSpacing: '0.02em', padding: '3px 9px', borderRadius: 'var(--radius-pill)',
            whiteSpace: 'nowrap', pointerEvents: 'none', zIndex: 1000, boxShadow: 'var(--shadow-2)',
          }}
        >
          {tooltip}
        </span>,
        document.body,
      )}
    </>
  );
}

/**
 * CloseIcon — Lucide "x" line glyph (1.7px stroke, matches icons.tsx). Used
 * for drawer close circles and the row "deactivate" affordance so no Unicode
 * text glyph (×/✕) leaks into the icon family (§1.6).
 */
export function CloseIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M18 6 6 18" />
      <path d="m6 6 12 12" />
    </svg>
  );
}

/** RestoreIcon — Lucide "rotate-ccw"; the reactivate/undo row affordance (§1.6). */
export function RestoreIcon({ size = 13 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
      <path d="M3 3v5h5" />
    </svg>
  );
}

/**
 * GhostCloseButton — the Ø36 drawer/card-header close circle (§2.18 header,
 * §2.8 ghost-circle tier): +1 luminance step over the panel, 44×44 hit area
 * via content-box padding (visual circle stays 36px).
 */
export function GhostCloseButton({ label = 'Close', onClick }: { label?: string; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="pressable focus-ring"
      style={{
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        width: 36, height: 36, padding: 4, margin: -4, boxSizing: 'content-box',
        border: 'none', borderRadius: 'var(--radius-pill)', backgroundClip: 'content-box',
        // QA R2 regression fix — see GhostIconButton above: `backgroundColor`
        // avoids the `background` shorthand's implicit backgroundClip reset.
        backgroundColor: 'var(--surface-2)', color: 'var(--text-muted)', cursor: 'pointer', flexShrink: 0,
        transition: 'color var(--dur-fast) var(--ease-out), background-color var(--dur-fast) var(--ease-out)',
      }}
      onMouseEnter={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.backgroundColor = 'var(--surface-3)';
        el.style.backgroundClip = 'content-box';
        el.style.color = 'var(--text)';
      }}
      onMouseLeave={(e) => {
        const el = e.currentTarget as HTMLButtonElement;
        el.style.backgroundColor = 'var(--surface-2)';
        el.style.backgroundClip = 'content-box';
        el.style.color = 'var(--text-muted)';
      }}
    >
      <CloseIcon size={16} />
    </button>
  );
}
